import { useState } from "react";

import {
  IMasterLanguage,
  IMasterTranslation,
  IUpsertMasterTranslationRequest,
} from "../../../@types/admin/AdminMasterTranslations";
import { useToast } from "../../../hooks/useToast";
import { serverErrorMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  CheckboxField,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  ReadOnlyValue,
} from "../shared";
import { TranslationInput } from "./AdminTranslations.styles";

interface IUpsertMasterTranslationModalProps {
  translationKey: string;
  usedLocation: string;
  /** Set = edit that translation; unset = add a language to the key. */
  translation?: IMasterTranslation;
  languages: IMasterLanguage[];
  /** Languages the key already has, excluded from the add-language select. */
  existingCodes: string[];
  isSaving: boolean;
  onUpsert: (request: IUpsertMasterTranslationRequest) => Promise<void>;
  onClose: () => void;
}

/**
 * Add or edit one translation of a master key — Retool's
 * `modalAddTranslationFrame` / `modalEditTranslation` (EN-5178). The API is a
 * single upsert on (key, language, location); this modal is both faces of it.
 * Unlike Retool, editing keeps the language fixed — moving a value to another
 * language is a delete + add, not a mutation of the row's identity.
 */
export function UpsertMasterTranslationModal({
  translationKey,
  usedLocation,
  translation,
  languages,
  existingCodes,
  isSaving,
  onUpsert,
  onClose,
}: IUpsertMasterTranslationModalProps) {
  const { showToast } = useToast();
  const isEdit = Boolean(translation);

  const [languageCode, setLanguageCode] = useState(
    translation?.languageCode ?? ""
  );
  const [value, setValue] = useState(translation?.value ?? "");
  const [archived, setArchived] = useState(translation?.archived ?? false);

  const availableLanguages = languages.filter(
    (language) => !existingCodes.includes(language.code)
  );

  const canSubmit = languageCode !== "" && value.trim() !== "";

  const handleSave = async () => {
    if (!canSubmit) return;

    try {
      await onUpsert({
        key: translationKey,
        languageCode,
        usedLocation,
        value,
        archived,
      });
      showToast(
        isEdit
          ? "The translation was updated successfully."
          : "The translation was added successfully.",
        "success"
      );
      onClose();
    } catch (error) {
      showToast(
        serverErrorMessage(error) ??
          "There was an error saving this translation.",
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>{isEdit ? "Edit translation" : "Add translation"}</ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel>Key</FilterLabel>
          <ReadOnlyValue css={{ overflowWrap: "anywhere" }}>
            {translationKey}
          </ReadOnlyValue>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="master-translation-language">
            Language
          </FilterLabel>
          {isEdit ? (
            <ReadOnlyValue>{translation!.languageCode}</ReadOnlyValue>
          ) : (
            <>
              <AdminSelectField
                id="master-translation-language"
                value={languageCode}
                disabled={isSaving}
                onChange={(e) => setLanguageCode(e.target.value)}
              >
                <option value="">Select a language</option>
                {availableLanguages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.name} ({language.code})
                  </option>
                ))}
              </AdminSelectField>
              {availableLanguages.length === 0 && (
                <FieldHint>
                  This key already has every language in the catalogue.
                </FieldHint>
              )}
            </>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="master-translation-value">Value</FilterLabel>
          <TranslationInput
            id="master-translation-value"
            rows={4}
            value={value}
            disabled={isSaving}
            onChange={(e) => setValue(e.target.value)}
          />
        </FilterGroup>

        <CheckboxField>
          <input
            type="checkbox"
            checked={archived}
            disabled={isSaving}
            onChange={(e) => setArchived(e.target.checked)}
          />
          Archived
        </CheckboxField>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={!canSubmit || isSaving}
            onClick={handleSave}
          >
            {isSaving ? "Saving..." : "Save"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
